import {EventEmitter, Injectable} from '@angular/core';
import {AbstractControl} from '@angular/forms';

import 'rxjs/add/observable/of';
import 'rxjs/add/operator/do';
import 'rxjs/add/operator/delay';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {DialogComponent} from './dialog.component';
import {CvParsedModel} from '../cvs/cv.parsed.model';
import {CvsComponent} from '../cvs/cvs.component';
import {DialogParseCvComponent} from './dialog.parse.cv.component';
import {ProjectModel} from '../ecv-project/project.model';
import {HolidayModel} from '../ecv-project/holiday/holiday.model';
import {DialogHolidayComponent} from '../ecv-project/dialog/dialog.holiday.component';
import {CvsModel} from '../cvs/cvs.model';
import {DialogCVPreviewComponent} from './dialog.cv-preview.component';
import {TimeTrackerComponent} from '../ecv-project/time-tracker/time-tracker.component';
import {DialogWorkMonthApproveComponent} from '../ecv-project/dialog/dialog.work-month-approve.component';
import {DialogExpensesClaimComponent} from '../ecv-project/dialog/dialog.expenses-claim.component';
import {WorkTimeRangeModel} from '../ecv-project/time-tracker/time-tracker-model/work-time-range.model';
import {TimeTrackerWorkDayComponent} from '../ecv-project/time-tracker/time-tracker-work-day/time-tracker-work-day.component';
import {DialogWorkTimeRangeComponent} from '../ecv-project/dialog/dialog.work-time-range.component';
import {DialogWorkTimeRangeDeleteComponent} from '../ecv-project/dialog/dialog.work-time-range-delete.component';
import {RankingComponent} from '../ranking/ranking.component';
import {GoogleScholarPublicationModel} from './google-scholar-publication.model';
import {DialogGoogleScholarPublicationsComponent} from './dialog.google-scholar-publications.component';
import {NetworkComponent} from '../network/network.component';
import {GithubProfileModel} from './github-profile.model';
import {DialogGithubProfilesComponent} from './dialog.github-profiles.component';
import {DialogWikipediaAnchorsComponent} from '../ecv-project/dialog/dialog.wikipedia-anchors/dialog.wikipedia-anchors.component';
import {WikipediaAnchorModel} from '../shared/semantic-enrichment/wikipedia-anchor.model';
import {DialogDictAliasComponent} from './dialog.dict-alias.component';
import {DictPanelComponent} from '../dict-panel/dict-panel.component';


@Injectable()
export class DialogService {
  constructor(private modalService: NgbModal) {
  }

  public open(title: string, dictType: number, control: AbstractControl, maxLength: number,
              data: any, property: string, onClose?: EventEmitter<any>) {
    const modalRef = this.modalService.open(DialogComponent);
    modalRef.componentInstance.closeEmitter = onClose;
    modalRef.componentInstance.maxLength = maxLength;
    modalRef.componentInstance.title = title;
    modalRef.componentInstance.dictModel.dictType = dictType;
    modalRef.componentInstance.dictModel.description = control.value;
    modalRef.componentInstance.dictModel.descriptionEng = control.value;
    modalRef.componentInstance.control = control;
    modalRef.componentInstance.changeRef.markForCheck();
    modalRef.componentInstance.changeDict.subscribe(event => {
      data[property] = event.id;
      onClose.emit(event);
    });
  }

  public openGoogleScholarPublicationsModal(publications: GoogleScholarPublicationModel[], parent: NetworkComponent) {
    const modalRef = this.modalService.open(DialogGoogleScholarPublicationsComponent);
    modalRef.componentInstance.publications = publications;
    modalRef.componentInstance.parent = parent;
  }

  public openCvParseModal(model: CvParsedModel, parent: CvsComponent) {
    const modalRef = this.modalService.open(DialogParseCvComponent);
    if (parent.userRefNumber) {
      model.refNumber = parent.userRefNumber;
    }
    modalRef.componentInstance.model = model;
    model.firstNameAccepted = !!(model.firstName);
    model.lastNameAccepted = !!(model.lastName);
    model.emailAccepted = !!(model.email);
    model.phoneAccepted = !!(model.phone);
    if (model.competences) {
      for (const element of model.competences) {
        element.accepted = true;
      }
      for (const element of model.experience) {
        element.accepted = true;
        if (element.from.date === '-1') {
          element.from.date = null;
        }
        if (element.to.date === '-1') {
          element.to.date = null;
        }
      }
      for (const element of model.education) {
        element.accepted = true;
        if (element.from.date === '-1') {
          element.from.date = null;
        }
        if (element.to.date === '-1') {
          element.to.date = null;
        }
      }
      for (const element of model.socialMedia) {
        element.accepted = true;
      }
      for (const element of model.repositories) {
        element.accepted = true;
      }
    }
    modalRef.componentInstance.changeRef.markForCheck();
    modalRef.componentInstance.cvParsed.subscribe(event => {
      parent.cvParsed.emit();
    });
  }

  public openHolidayModal(projectsModel: ProjectModel[], holidayModel: HolidayModel) {
    const modalRef = this.modalService.open(DialogHolidayComponent);
    modalRef.componentInstance.projectsModel = projectsModel;
    modalRef.componentInstance.holidayDetails = holidayModel;
    if (projectsModel.length !== 0) {
      modalRef.componentInstance.holidayDetails.projectId = projectsModel[0].id;
    }
  }

  public openGithubProfilesModal(profilesModel: GithubProfileModel[], parent: NetworkComponent) {
    const modalRef = this.modalService.open(DialogGithubProfilesComponent, {windowClass: 'custom-modal-sm'});
    modalRef.componentInstance.profiles = profilesModel;
    modalRef.componentInstance.parent = parent;
  }

  public openRankingCVModal(cvs: CvsModel[], parent: RankingComponent) {
    const modalRef = this.modalService.open(DialogCVPreviewComponent);
    modalRef.componentInstance.cvs = cvs;
    modalRef.componentInstance.cvsContentCache = new Array(cvs.length);
    modalRef.componentInstance.updateCurrentContent(0);

    modalRef.componentInstance.closeEmitter.subscribe(() => {
      parent.onModalClose();
    });
  }

  public openWorkMonthApproveModal(parent: TimeTrackerComponent) {
    const modalRef = this.modalService.open(DialogWorkMonthApproveComponent, {windowClass: 'custom-modal-sm'});
    modalRef.componentInstance.approveWorkMonthRequest.subscribe(event => {
      parent.approveWorkMonth(parent.workMonth.id);
    });
  }

  public openExpensesClaimModal(parent: TimeTrackerComponent, currentExpenseClaim: number) {
    const modalRef = this.modalService.open(DialogExpensesClaimComponent);
    modalRef.componentInstance.value = currentExpenseClaim;
    modalRef.componentInstance.editExpensesClaimRequest.subscribe(event => {
      parent.editExpenseClaims(parent.workMonth.id, event);
    });
  }

  public openWorkTimeRangeModal(model: WorkTimeRangeModel, parent: TimeTrackerWorkDayComponent, newWorkTimeRangeFlag: boolean = true) {
    const modalRef = this.modalService.open(DialogWorkTimeRangeComponent, {windowClass: 'custom-modal-sm'});
    modalRef.componentInstance.newWorkTimeRangeFlag = newWorkTimeRangeFlag;
    modalRef.componentInstance.readOnly = model ?
      model.status === 'APPROVED' || model.status === 'INVOICED' || model.status === 'INVOICE_PAID'
      : false;
    modalRef.componentInstance.workDayId = parent.data.id;
    modalRef.componentInstance.workDayDate = parent.workDayDate;
    modalRef.componentInstance.currentWorkTimeRange = model;

    modalRef.componentInstance.addWorkTimeRangeEvent.subscribe(data => {
      parent.addWorkTimeRangeRequest.emit(data);
    });
    modalRef.componentInstance.updateWorkTimeRangeEvent.subscribe(data => {
      parent.saveWorkTimeRangeRequest.emit(data);
    });
    modalRef.componentInstance.deleteWorkTimeRangeEvent.subscribe(data => {
      parent.deleteWorkTimeRangeRequest.emit(data);
    });
  }

  public openWorkTimeRangeDeleteModal(model: WorkTimeRangeModel, parent: DialogWorkTimeRangeComponent) {
    const modalRef = this.modalService.open(DialogWorkTimeRangeDeleteComponent);
    modalRef.componentInstance.currentWorkTimeRange = model;

    modalRef.componentInstance.deleteWorkTimeRangeEvent.subscribe(reason => {
      parent.deleteWorkTimeRangeWithReason(reason);
    });

    modalRef.componentInstance.closeEmitter.subscribe(() => {
      parent.showComponent();
    });
  }

  public openWikipediaAnchorsModal(model: WikipediaAnchorModel[]) {
    const modalRef = this.modalService.open(DialogWikipediaAnchorsComponent);
    modalRef.componentInstance.model = model;
  }

  public openDictAliasModal(parent: DictPanelComponent) {
    const modalRef = this.modalService.open(DialogDictAliasComponent);
    modalRef.componentInstance.parent = parent;
  }
}



// WEBPACK FOOTER //
// C:/Users/Sylwia/Desktop/12.02/ecv_manager_frontend/src/app/dialog/dialog.service.ts